import { isNull, isUndefined } from 'lodash/fp';
import React, { useCallback, useMemo, useState } from 'react';

import { PieChartAnalyticsData } from '../../../shared/analytics/PieChart';
import { Document, Agency, User } from '../../../shared/icons';
import { LoadingDots } from '../../../shared/loading-dots/LoadingDots';
import { CustomDocumentQueryAnalytics, ChatBotAnalyticsPieCharts } from './store';
import styles from './ChatBot.module.scss';
import { ChatBotPieChartWrapper } from './ChatBotPieChartWrapper';

const { red, green } = styles;

interface SelectedClientOrUser {
    id: number;
    name: string;
}

interface ChatBotPieChartsProps {
    customQueryAnalytics: CustomDocumentQueryAnalytics;
    isFetchingAnalytics: boolean;
    clientOrUserKeys: string[];
    chartDimensions: {
        height: number;
        width: number;
    };
    selectedClient: SelectedClientOrUser | null;
    selectedUser: SelectedClientOrUser | null;
    setSelectedClientOrUser: (id: number, name: string) => void;
}

export const ChatBotPieCharts: React.FC<ChatBotPieChartsProps> = ({
    customQueryAnalytics,
    isFetchingAnalytics,
    clientOrUserKeys,
    chartDimensions,
    selectedClient,
    selectedUser,
    setSelectedClientOrUser
}) => {

    const { totalQuestionsAsked, totalQuestionsPassed, questionsAskedPerDocumentNameType, questionsAskedPerClient, questionsAskedPerClientUser } = customQueryAnalytics;

    const totalQuestions = useMemo(() => {
        const data: PieChartAnalyticsData[] = [{ key: 'total passed', value: totalQuestionsPassed, color: green }, { key: 'total failed', value: (totalQuestionsAsked - totalQuestionsPassed), color: red }];
        let titleExtension = 'all Clients';
        if (!isNull(selectedClient)) {
            titleExtension = selectedClient.name;
        }
        if (!isNull(selectedUser)) {
            titleExtension = selectedUser.name;
        }
        const title = 'Questions Asked by ' + titleExtension;
        const description = `A visualisation of all chat bot questions asked by ${titleExtension} and how many passed or failed our suitability checks`;
        const total = totalQuestionsAsked;
        return { data, title, description, total };
    }, [totalQuestionsAsked, totalQuestionsPassed, selectedClient, selectedUser]);

    const documentQuestions = useMemo(() => {
        const documentNameKeys = Object.keys(questionsAskedPerDocumentNameType);
        const data: PieChartAnalyticsData[] = documentNameKeys.map(key => ({ key, value: questionsAskedPerDocumentNameType[key] }));
        const title = 'Questions per Document Name Type';
        const description = 'A visualisation of all the chat bot questions asked for each document name type';
        return { data, title, description };
    }, [questionsAskedPerDocumentNameType]);

    const clientOrUserQuestions = useMemo(() => {
        const data: PieChartAnalyticsData[] = clientOrUserKeys.map(key => ({ key, value: (!isNull(selectedClient) ? questionsAskedPerClientUser! : questionsAskedPerClient!)[key].totalAsked, id: (!isNull(selectedClient) ? questionsAskedPerClientUser! : questionsAskedPerClient!)[key].id }));
        const title = `Questions asked per ${isNull(selectedClient) ? 'Client' : 'User'}`;
        const description = `A visualisation of all the chat bot questions asked for each ${isNull(selectedClient) ? 'Client' : 'User'}`;
        const icon = isNull(selectedClient) ? Agency : User;
        return { data, title, description, icon };
    }, [clientOrUserKeys, selectedClient, questionsAskedPerClient, questionsAskedPerClientUser]);

    const [pieOnePercentage, setPieOnePercentage] = useState<number | null>(null);
    const [pieTwoPercentage, setPieTwoPercentage] = useState<number | null>(null);
    const [pieThreePercentage, setPieThreePercentage] = useState<number | null>(null);

    const pieClick = useCallback((name: string) => {
        if (isUndefined(questionsAskedPerClientUser)) {
            const id = questionsAskedPerClient![name].id;
            setSelectedClientOrUser(id, name);
        } else {
            const id = questionsAskedPerClientUser[name].id;
            setSelectedClientOrUser(id, name);
        }
        setPieOnePercentage(null);
        setPieTwoPercentage(null);
        setPieThreePercentage(null);
    }, [questionsAskedPerClient, questionsAskedPerClientUser, setSelectedClientOrUser]);

    const pieHover = useCallback((data: PieChartAnalyticsData | null, id: string) => {
        const totalQuestionsAsked = totalQuestions.total;
        if (id === ChatBotAnalyticsPieCharts.TOTAL) {
            const percentage = !isNull(data) ? Math.round((data.value / totalQuestionsAsked) * 100) : null;
            setPieOnePercentage(percentage);
        }
        if (id === ChatBotAnalyticsPieCharts.DOCUMENT) {
            const percentage = !isNull(data) ? Math.round((data.value / totalQuestionsAsked) * 100) : null;
            setPieTwoPercentage(percentage);
        }
        if (id === ChatBotAnalyticsPieCharts.CLIENT_OR_USER) {
            const percentage = !isNull(data) ? Math.round((data.value / totalQuestionsAsked) * 100) : null;
            setPieThreePercentage(percentage);
        }
    }, [totalQuestions]);

    return (
        isFetchingAnalytics ? <LoadingDots /> :
            <>
                <ChatBotPieChartWrapper
                    data={totalQuestions.data}
                    title={totalQuestions.title}
                    description={totalQuestions.description}
                    total={totalQuestions.total}
                    dimensions={chartDimensions}
                    id={ChatBotAnalyticsPieCharts.TOTAL}
                    onHover={pieHover}
                    slicePercentage={pieOnePercentage}
                />
                <ChatBotPieChartWrapper
                    data={documentQuestions.data}
                    title={documentQuestions.title}
                    description={documentQuestions.description}
                    dimensions={chartDimensions}
                    id={ChatBotAnalyticsPieCharts.DOCUMENT}
                    onHover={pieHover}
                    slicePercentage={pieTwoPercentage}
                    icon={Document}
                />
                {clientOrUserQuestions.data.length > 0 &&
                    <ChatBotPieChartWrapper
                        data={clientOrUserQuestions.data}
                        title={clientOrUserQuestions.title}
                        description={clientOrUserQuestions.description}
                        dimensions={chartDimensions}
                        id={ChatBotAnalyticsPieCharts.CLIENT_OR_USER}
                        onDblClick={pieClick}
                        onHover={pieHover}
                        slicePercentage={pieThreePercentage}
                        icon={clientOrUserQuestions.icon}
                    />
                }
            </>
    );
};
